import { AxiosError } from 'axios';
import { apiService } from '@/shared/services/apiServices';
import { SERVICE_TYPE_ERROR_MESSAGES } from '../errors';
import type { ServiceType } from '../types/serviceTypeTypes';
import serviceTypeEndpoints from './serviceTypeEndpoints';

export interface ViewServiceTypeResult {
  success: boolean;
  data?: ServiceType;
  error?: string;
  notFound?: boolean;
  forbidden?: boolean;
}

const isInvalidUuidError = (errorData: unknown): boolean => {
  if (errorData && typeof errorData === 'object') {
    const message = (errorData as { message?: unknown }).message;
    return typeof message === 'string' && message === 'Invalid UUID format';
  }
  return false;
};

const extractErrorMessage = (errorData: unknown, status?: number): string => {
  if (status === 401 || status === 403) {
    return SERVICE_TYPE_ERROR_MESSAGES.UNAUTHORIZED;
  }
  if (status === 500) {
    return SERVICE_TYPE_ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
  }
  if (errorData && typeof errorData === 'object') {
    const obj = errorData as { message?: string; error?: string };
    if (obj.message) {
      return obj.message;
    }
    if (obj.error) {
      return obj.error;
    }
  }
  if (typeof errorData === 'string' && errorData.trim()) {
    return errorData;
  }
  return SERVICE_TYPE_ERROR_MESSAGES.DEFAULT_ERROR;
};

export const viewServiceType = async (
  serviceTypeId: string
): Promise<ViewServiceTypeResult> => {
  const trimmedId = serviceTypeId?.trim();
  if (!trimmedId) {
    return {
      success: false,
      error: SERVICE_TYPE_ERROR_MESSAGES.DEFAULT_ERROR,
    };
  }

  try {
    const result = await apiService.getAPI(
      `${serviceTypeEndpoints.SERVICE_TYPES}/${trimmedId}`,
      { skipGlobalErrorRedirect: true }
    );

    if (result.error && result.status === 403) {
      return { success: false, forbidden: true };
    }

    if (!result.error) {
      const payload = (result.data ?? {}) as {
        service_type?: ServiceType;
        data?: ServiceType;
      } & Partial<ServiceType>;
      const serviceType = (payload.service_type ??
        payload.data ??
        payload) as ServiceType;
      return {
        success: true,
        data: serviceType,
      };
    }

    if (result.isNetworkError) {
      return {
        success: false,
        error: SERVICE_TYPE_ERROR_MESSAGES.NETWORK_ERROR,
      };
    }

    if (isInvalidUuidError(result.data)) {
      return { success: false, notFound: true };
    }

    return {
      success: false,
      error: extractErrorMessage(result.data, result.status),
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const status = error.response?.status;
      const errorData = error.response?.data;
      const isNetworkError = !error.response && !!error.request;

      if (isNetworkError) {
        return {
          success: false,
          error: SERVICE_TYPE_ERROR_MESSAGES.NETWORK_ERROR,
        };
      }

      if (isInvalidUuidError(errorData)) {
        return { success: false, notFound: true };
      }

      return {
        success: false,
        error: extractErrorMessage(errorData, status),
      };
    }

    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : SERVICE_TYPE_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
